import { getTemplatePrograms } from './template-service';
import { getDateFilterProps, getTextFilterProps } from '../utils';
import {
  modalUtils,
  hasPermissionToRender,
  toSafeArray,
} from '@/app/core/utils';
import { JobViewModel } from '@/app/features/dashboard/jobs-list/models';
import { JobStatus } from '@/app/features/dashboard/jobs-list/JobStatus';
import { JobActions } from '@/app/features/dashboard/jobs-list/JobsActions';
import JobsService from '@/app/features/dashboard/jobs-list/jobs-service';
import { getCurrentFeatureCode } from '@/app/core/auth';
import { useEffect, useState, Key, useRef, useCallback } from 'react';
import { Table } from 'antd';
import { Text, VStack } from '@chakra-ui/react';
import {
  Job,
  JobsSearchResponse,
  JobStatus as JobStatusEnum,
  SortConfig,
  SortDirection,
  TemplatesPermissions,
} from '@ssar-app/domain';
import { SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import dayjs, { extend as dayJSExtend } from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import relativePlugin from 'dayjs/plugin/relativeTime';

dayJSExtend(utcPlugin);
dayJSExtend(relativePlugin);

const statusFilter: Record<string, string>[] = Object.entries(
  JobStatusEnum,
).map(([text, value]) => ({
  text,
  value,
}));

const fetchJobs = getTemplatePrograms;

interface APSProgramsListProps {
  templateId: string;
}

export const APSProgramsList = (props: APSProgramsListProps) => {
  const [jobs, setJobs] = useState<JobViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const filtersPassed = useRef<Record<string, (Key | boolean)[] | null>>({});
  const sorterPassed = useRef<
    SorterResult<JobViewModel> | SorterResult<JobViewModel>[]
  >([]);
  const setTimeoutId = useRef(0);

  const columns = [
    {
      title: 'Status',
      sorter: true,
      dataIndex: 'status',
      width: 200,
      filters: statusFilter,
      render: (_text: string, row: Job) => {
        return <JobStatus {...{ ...row, handleUpdate: handleUpdate }} />;
      },
    },
    {
      title: 'Actions',
      width: 175,
      render: (_text: string, row: Job) => {
        return (
          <JobActions
            record={row}
            setIsLoading={setIsLoading}
            forJobs={false}
          />
        );
      },
    },
    {
      title: 'File Name',
      dataIndex: ['templateMetaData', 'clientFileName'],
      sorter: true,
      key: 'clientFileName',
      ...getTextFilterProps('File Name'),
      render: (text: string | undefined, _row: Job) => {
        return <>{text}</>;
      },
    },
    {
      title: 'Number of Records',
      dataIndex: ['templateMetaData', 'numberOfRecords'],
      sorter: true,
      key: 'numberOfRecords',
      ...getTextFilterProps('Number of Records'),
      render: (text: string | undefined, _row: Job) => {
        return <>{text}</>;
      },
    },
    {
      title: 'Submitted Date',
      dataIndex: 'submittedDate',
      sorter: true,
      ...getDateFilterProps(),
      render: (text: string) => {
        return dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ');
      },
    },
    {
      title: 'Completed Date',
      dataIndex: 'completedDate',
      sorter: true,
      ...getDateFilterProps(),
      render: (text: string, row: Job) => {
        if (
          (row.completedDate && row.status === JobStatusEnum.CANCELLED) ||
          row.status === JobStatusEnum.COMPLETED
        ) {
          return dayjs(text).format('YYYY-MMM-DD HH:mm:ss  [GMT]ZZ');
        }
        return null;
      },
    },
    {
      title: 'Submitted Email',
      dataIndex: 'userId',
      sorter: true,
      key: 'submittedEmail',
      ...getTextFilterProps('Submitted Email'),
      render: (text: string | undefined, _row: Job) => {
        return <>{text}</>;
      },
    },
  ].filter((a) => a !== null);

  const fetchData = (
    paginationInput: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<JobViewModel> | SorterResult<JobViewModel>[] = [],
  ): void => {
    setIsLoading(true);

    filtersPassed.current = filters;
    sorterPassed.current = sorter;

    const sortConfig: SortConfig[] = [];

    toSafeArray(sorter).forEach((a) => {
      if (a.field) {
        const dir =
          a.order === 'descend'
            ? SortDirection.Descending
            : a.order === 'ascend'
            ? SortDirection.Ascending
            : null;
        if (dir) {
          sortConfig.push({
            field: a.field.toString(),
            direction: dir,
          });
        }
      }
    });

    const searchModel = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      paging: {} as any,
      sortBy: sortConfig,
      filter: filters,
    };

    fetchJobs(props.templateId, searchModel)
      .then((jobsResponse: JobsSearchResponse) => {
        const { data } = jobsResponse;
        setJobs(
          (data || []).map((a) => ({
            ...a,
            status: a.status,
            name: a.programName,
          })),
        );
      })
      .catch((err) => {
        console.error(err);

        modalUtils.showError(err, 'Error while fetching the Programs');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchDataWrapper = useCallback(
    () => fetchData({}, {}, []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    fetchDataWrapper();
  }, [fetchDataWrapper]);

  const handleUpdate = (action: string, externalSystemRef: string): void => {
    setIsLoading(true);
    clearTimeout(setTimeoutId.current);
    JobsService.updateJob(action, externalSystemRef)
      .then((res) => {
        if (res.data?.status && res.data?.status.toLowerCase() === 'success') {
          modalUtils.showSuccessMessage(
            res.data?.message || 'State updated successfully.',
          );
          fetchData({}, filtersPassed.current, sorterPassed.current);
        } else {
          modalUtils.showError(
            res.data?.message || 'Error while performing action.',
          );
        }
      })
      .catch((err) => {
        modalUtils.showError(err, 'Error while performing action.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      {hasPermissionToRender(
        getCurrentFeatureCode(),
        TemplatesPermissions.MayViewTemplates,
      ) ? (
        <VStack backgroundColor={'gray.100'} p={4} w={'full'}>
          <Text w={'full'} fontSize={'md'}>
            Program Details
          </Text>
          <Table
            onChange={fetchData}
            showSorterTooltip={false}
            className='table-data-list aps-programs-table'
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rowKey={(row) => row.id || (row as any)['_id']}
            loading={isLoading}
            dataSource={jobs}
            columns={columns}
            pagination={false}
          />
        </VStack>
      ) : (
        <div>No data/permission.</div>
      )}
    </>
  );
};
